import type { ScoutingProducts } from '@/marketing/models/campaignScoutingDetails';
import type { ScoutingViewRow } from './scoutingView';

export type ScoutingCheckFilter = 'all' | 'review' | 'unavailable' | 'clear';
export const BRAND_REVIEW_LABELS = {
  awaiting: 'Awaiting brand review',
  picked: 'Brand approved',
  rejected: 'Rejected in brand review',
  notApplicable: 'Not applicable',
};
export function scoutingCheckStatus(row: ScoutingViewRow): Exclude<ScoutingCheckFilter, 'all'> {
  const checks = row.details?.checks;
  if (checks?.some((check) => check.status === 'review')) {
    return 'review';
  }
  if (!checks?.length || checks.some((check) => check.status === 'unavailable')) {
    return 'unavailable';
  }
  return 'clear';
}
export function matchesScoutingChecks(row: ScoutingViewRow, filter: ScoutingCheckFilter): boolean {
  if (filter === 'all') {
    return true;
  }
  if (filter === 'unavailable') {
    return !row.details?.checks?.length || row.details.checks?.some((check) => check.status === 'unavailable');
  }
  return scoutingCheckStatus(row) === filter;
}
export function scoutingName(row: ScoutingViewRow): string {
  return (
    [row.details?.contact.firstName, row.details?.contact.lastName].filter(Boolean).join(' ') ||
    row.details?.displayName ||
    ''
  );
}
export function scoutingProductLabels(products: ScoutingProducts | undefined): string[] {
  if (!products) {
    return [];
  }
  const skus =
    products.selectedSkus ??
    (products.choiceCount == null && !products.groups.length ? products.options.map((option) => option.sku) : []);
  return skus.map((sku) => products.options.find((option) => option.sku === sku)?.label ?? sku);
}
export function displayScoutingNumber(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? '—' : value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
export function displayScoutingDate(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? '—' : date.toISOString().slice(0, 10);
}
export function safeScoutingUrl(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) ? url.href : undefined;
  } catch {
    return undefined;
  }
}
export function validScoutingProducts(products: ScoutingProducts, selected: string[]): boolean {
  if (!selected.length || new Set(selected).size !== selected.length) {
    return false;
  }
  if (selected.some((sku) => !products.options.some((option) => option.sku === sku && !option.disabled))) {
    return false;
  }
  if (products.groups.length) {
    return (
      selected.every((sku) =>
        products.groups.some((group) =>
          products.options.some((option) => option.sku === sku && option.groupId === group.groupId),
        ),
      ) &&
      products.groups.every(
        (group) =>
          selected.filter((sku) =>
            products.options.some((option) => option.sku === sku && option.groupId === group.groupId),
          ).length === group.choiceCount,
      )
    );
  }
  return products.choiceCount != null && selected.length === products.choiceCount;
}
